<template>
  <div class="feedback-form">
    <step-header
      :title="headerTitle"
      :subtitle="locationName"
    />
    <div class="body">
      <loading-status v-if="isSubmitting" />
      <div class="testimonial">
        <p
          v-if="isPrivate"
          class="prompt"
        >
          Your feedback is important to our practice and will be kept private.
        </p>

        <div
          v-if="requiresRating"
          class="rating-container"
        >
          <star-rating v-model="rating" />
          <div
            v-if="showRatingError"
            class="review-required review-required-text"
          >
            * Required
          </div>
        </div>

        <div>
          <pp-input-textarea
            ref="content"
            v-model="content"
            label="Describe your experience"
            rows="6"
            hide-details
            data-cy="review-content"
            :error="showContentError"
            :aria-invalid="showContentError ? 'true' : 'false'"
            :aria-describedby="showContentError ? 'content-error' : null"
          />
          <div
            v-if="showContentError"
            id="content-error"
            class="review-required review-required-text"
          >
            * Required
          </div>
        </div>

        <div
          v-if="submitError"
          role="alert"
          data-cy="submit-error"
        >
          <pp-form-message
            type="error"
            text="Something went wrong. Please try again."
          />
        </div>

        <pp-button
          class="btn-submit-review"
          type="primary"
          text="Submit"
          data-cy="submit-review"
          :disabled="isSubmitting"
          @click="onSubmit"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { required, minValue } from 'vuelidate/lib/validators'
import StepHeader from '@/components/StepHeader/StepHeader.vue'
import StarRating from '@/components/StarRating/StarRating.vue'
import LoadingStatus from '@/components/LoadingStatus/LoadingStatus.vue'
import { REVIEW_TYPE, SOURCE } from '@/constants'
import { trackView, trackSubmit } from '@/analytics'

function notBlank (value) {
  return typeof value === 'string' && value.trim().length > 0
}

export default {
  name: 'FeedbackForm',
  components: { StepHeader, StarRating, LoadingStatus },
  data () {
    return {
      rating: 0,
      content: ''
    }
  },
  validations () {
    const rules = {
      content: { required, notBlank }
    }
    if (this.requiresRating) {
      rules.rating = { required, minValue: minValue(1) }
    }
    return rules
  },
  computed: {
    ...mapGetters({
      headerTitle: 'reputation/headerTitle',
      locationName: 'reputation/locationName',
      reviewType: 'reputation/reviewType',
      source: 'reputation/source',
      isSubmitting: 'reputation/isSubmitting',
      submitError: 'reputation/submitError',
      submitted: 'reputation/submitted',
      analyticsDims: 'reputation/analyticsDims'
    }),
    isPublic () {
      return this.reviewType === REVIEW_TYPE.PUBLIC
    },
    isPrivate () {
      return this.reviewType === REVIEW_TYPE.PRIVATE
    },
    // Public reviews always collect stars. PO private reviews do too, because
    // that API requires a rating of 1–5. PG private reviews do not.
    requiresRating () {
      return this.isPublic || (this.isPrivate && this.source === SOURCE.PO)
    },
    storedRating () {
      return this.$store.getters['reputation/rating']
    },
    storedContent () {
      return this.$store.getters['reputation/content']
    },
    showRatingError () {
      return this.requiresRating && this.$v.rating && this.$v.rating.$dirty && this.$v.rating.$invalid
    },
    showContentError () {
      return this.$v.content.$dirty && this.$v.content.$invalid
    }
  },
  created () {
    this.rating = this.storedRating || 0
    this.content = this.storedContent || ''
    trackView(this, this.isPublic ? 'testimonial_screen' : 'feedback_screen', this.analyticsDims)
  },
  methods: {
    ...mapActions({
      submitReview: 'reputation/submitReview',
      setRating: 'reputation/setRating',
      setContent: 'reputation/setContent'
    }),
    validate () {
      this.$v.$touch()
      return !this.$v.$invalid
    },
    async onSubmit () {
      if (this.submitted) {
        this.$router.replace({ name: 'Confirmation' })
        return
      }
      if (!this.validate()) return

      this.setRating(this.requiresRating ? this.rating : 0)
      this.setContent(this.content.trim())

      await this.submitReview()

      if (!this.submitError) {
        trackSubmit(this, this.isPublic ? 'testimonial' : 'feedback', this.analyticsDims)
        this.$router.push({ name: 'Confirmation' })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.testimonial {
  padding: 0 50px;

  @include mobile {
    padding: 0;
  }
}

.prompt {
  color: $text-primary;
  font-size: 16px;
  line-height: 1.4;
  margin-bottom: 16px;
  margin-top: 8px;
  text-align: left;
}

.rating-container {
  margin: 10px 0;
}

.review-required-text {
  color: $text-error;
  font-size: 14px;
  margin-top: 10px;
}

.btn-submit-review {
  display: block;
  height: 36px;
  margin: 20px 0;
  width: 100%;
}

</style>
